import { List } from "immutable";
import * as QueryString from "querystringify";
import React from "react";
import * as BS from "react-bootstrap";
import { connect } from "react-redux";
import { RouteComponentProps, withRouter } from "react-router-dom";
import { push } from "react-router-redux";
import * as redux from "redux";
import { PostJobForm } from "./PostJobForm";
import * as Actions from "../../../actions";
import * as Comp from "../../../components";
import { Paths } from "../../../constants";
import { CareerCenterStatuses } from "../../../domain";
import * as Forms from "../../../forms";
import * as M from "../../../models";
import { Api } from "../../../services/Api";
import { AppState } from "../../../store";

const mapStateToProps = (state: AppState, ownProps: OwnProps | undefined): ConnectedState => {
    let hasMemberRole = false;
    if (state.auth.accessToken && state.auth.refreshToken) {
        const decodedToken =  new M.Auth.AuthTokenDecoded(state.auth.accessToken, state.auth.refreshToken);
        hasMemberRole = decodedToken.hasMemberRole();
    }

    return {
        api: new Api(state),
        countries: state.countries.countries,
        userIsMember: hasMemberRole,
    };
};

const mapDispatchToProps = (dispatch: redux.Dispatch<AppState>): ConnectedDispatch => ({
    goto: (url: string) => dispatch(push(url)),
    notifyInfo: (message: string, icon?: string) => dispatch(Actions.Noti.notifyInfo(message, icon)),
    updateQuery: (query: UrlQuery, overwrite?: boolean) => dispatch(Actions.Router.updateQueryParams(query, overwrite)),
});

interface UrlQuery {
    copy?: string;
}

type OwnProps = RouteComponentProps<{
    guid?: string;
}> & {
};

interface ConnectedState {
    api: Api;
    countries: List<M.Country.Country>;
    userIsMember: boolean;
}

interface ConnectedDispatch {
    goto: (url: string) => void;
    notifyInfo: (message: string, icon?: string) => void;
    updateQuery: (query: UrlQuery, overwrite?: boolean) => void;
}

interface OwnState {
    errors: string[];
}

type CompinedProps = ConnectedState & ConnectedDispatch & OwnProps;

class PostJobComponent extends Comp.Base.BaseComponent<CompinedProps, OwnState> {

    private _form: PostJobForm | null;

    public constructor(props: any) {
        super(props);
        this.state = {
            errors: [],
            loading: this.props.match.params.guid !== undefined
                || this.props.location.search.indexOf("copy") > -1,
        };
    }

    public async componentDidMount() {
        this._isMounted = true;

        if (this._form)
            this._form.setUserIsMember(this.props.userIsMember);

        const urlQuery = QueryString.parse(this.props.location.search) as UrlQuery;
        const guid = this.props.match.params.guid;

        if (guid) {
            // load previously entered job post
            const qp = { queryParams: { guid } };
            const result = await this.props.api.get<M.CareerCenter.JobPost>(Api.endpoints.users.careerCenter.job, qp);

            if (!this._isMounted)
                return;

            if (result.isSuccess && result.data) {
                if (result.data.statusId !== CareerCenterStatuses.Created.id) {
                    this.props.notifyInfo("The requested job post has already been posted.");
                    this.props.goto(Paths.CareerCenter.Job.Confirmation + "/" + guid);
                    return;
                }
                else {
                    if (this._form)
                        this._form.setFormDataFromJob(result.data);
                }
            }
            else {
                const errors =  M.Result.Result.errorsOrMessage(result);
                this.setState({ ...this.state, errors });
            }

            this.toggleLoading(false);
        }
        else if (urlQuery.copy) {
            this.toggleLoading(true);
            const qp = { queryParams: { guid: urlQuery.copy } };
            const result = await this.props.api.get<M.CareerCenter.JobPost>(Api.endpoints.users.careerCenter.job, qp);

            if (!this._isMounted)
                return;

            if (result.isSuccess && result.data) {
                if (this._form) {
                    // need to set IDs to 0 to simulate new form
                    result.data.guid = "";
                    result.data.id = 0;
                    this._form.setFormDataFromJob(result.data);
                }
            }
            else {
                this.props.updateQuery({ copy: "" });
            }

            this.toggleLoading(false);
        }
    }

    public componentWillUnmount() {
        this._isMounted = false;
    }

    public render() {
        let details = null;

        if (this.state.errors.length) {
            details = <BS.Alert bsStyle="warning">
                <h3>The requested job post could not be retrieved.</h3>
                <ul>
                    {this.state.errors.map((e, i) => (<li key={i}>{e.toString()}</li>))}
                </ul>
            </BS.Alert>;
        }
        else {
            details = <PostJobForm
                api={this.props.api}
                countries={this.props.countries}
                onSubmit={(model, body) => this.onSubmit(model, body)}
                ref={(ref) => this._form = ref}
                userIsMember={this.props.userIsMember}
            />;
        }

        return <Comp.Page.PublicPage pageTitle="Post job" style={{position: "relative", minHeight: "300px"}}>
            <Comp.Generic.LoadingSpinner show={this.state.loading} />
            {details}
        </Comp.Page.PublicPage>;
    }

    private async onSubmit(model: Forms.CareerCenter.PostJobData, body: any) {
        if (!this._form)
            return;

        if (!this._form.state.modelChanged && this.props.match.params.guid) {
            this.props.goto(`${Paths.CareerCenter.Job.Review}/${this.props.match.params.guid}`);
            return;
        }
        else {
            this._form.toggleProcessing(true);

            body.guid = this.props.match.params.guid;
            const result = await this.props.api.post<M.CareerCenter.JobPost>(Api.endpoints.users.careerCenter.createJob, { body: JSON.stringify(body) });

            if (!this._isMounted || !this._form)
                return;

            if (result.isSuccess && result.data) {
                if (!this.props.match.params.guid)
                    this.props.goto(`${Paths.CareerCenter.Job.Post}/${result.data.guid}`); // need this for back button
                this.props.goto(`${Paths.CareerCenter.Job.Review}/${result.data.guid}`);
            }
            else {
                this._form.setFormErrors(M.Result.Result.errorsOrMessage(result), undefined, true);
            }

            if (this._isMounted)
                this._form.toggleProcessing(false);
        }
    }
}

export const PostJob: React.ComponentClass<OwnProps> = connect(mapStateToProps, mapDispatchToProps)(withRouter(PostJobComponent) as any);



// WEBPACK FOOTER //
// ./src/containers/careerCenter/postJob/PostJob.tsx